import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { message } from 'antd';

interface User {
  username: string;
  fullName: string;
  userType: 'super_admin' | 'admin' | 'device_editor';
  isMainAccount: boolean;
  isAdmin: boolean; // 保持向后兼容
}

interface AuthContextType {
  user: User | null;
  token: string | null;
  login: (username: string, password: string) => Promise<boolean>;
  logout: () => void;
  refreshUser: () => Promise<boolean>;
  isLoading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // 从localStorage恢复token
  useEffect(() => {
    const savedToken = localStorage.getItem('auth_token');
    if (savedToken) {
      setToken(savedToken);
      verifyToken(savedToken);
    } else {
      setIsLoading(false);
    }
  }, []);

  // 验证token有效性
  const verifyToken = async (tokenToVerify: string) => {
    try {
      const response = await fetch('/api/auth/verify', {
        headers: {
          'Authorization': `Bearer ${tokenToVerify}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setUser(data.data);
          setToken(tokenToVerify);
        } else {
          // Token无效，清除
          localStorage.removeItem('auth_token');
          setToken(null);
          setUser(null);
        }
      } else {
        // Token无效，清除
        localStorage.removeItem('auth_token');
        setToken(null);
        setUser(null);
      }
    } catch (error) {
      console.error('Token verification failed:', error);
      localStorage.removeItem('auth_token');
      setToken(null);
      setUser(null);
    } finally {
      setIsLoading(false);
    }
  };

  // 登录函数
  const login = async (username: string, password: string): Promise<boolean> => {
    try {
      const response = await fetch('/api/auth/login', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ username, password })
      });

      const data = await response.json();

      if (response.ok && data.success) {
        const {
          token: newToken,
          username: returnedUsername,
          fullName,
          userType,
          isMainAccount
        } = data.data;

        // 保存token到localStorage
        localStorage.setItem('auth_token', newToken);
        setToken(newToken);
        setUser({
          username: returnedUsername,
          fullName: fullName || returnedUsername,
          userType: userType || 'admin',
          isMainAccount: isMainAccount || false,
          isAdmin: true
        });

        message.success('登录成功');
        return true;
      } else {
        message.error(data.error || '登录失败');
        return false;
      }
    } catch (error) {
      console.error('Login error:', error);
      message.error('登录过程中发生错误');
      return false;
    }
  };

  // 登出函数
  const logout = async () => {
    try {
      if (token) {
        // 通知服务器登出（可选）
        await fetch('/api/auth/logout', {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
      }
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      // 清除本地状态
      localStorage.removeItem('auth_token');
      setToken(null);
      setUser(null);
      message.success('已退出登录');
    }
  };

  // 刷新用户信息
  const refreshUser = async (): Promise<boolean> => {
    try {
      const currentToken = token || localStorage.getItem('auth_token');
      if (!currentToken) {
        return false;
      }

      const response = await fetch('/api/auth/refresh', {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${currentToken}`
        }
      });

      const data = await response.json();

      if (response.ok && data.success) {
        const {
          token: newToken,
          username: returnedUsername,
          fullName,
          userType,
          isMainAccount
        } = data.data;

        // 更新token和用户信息
        localStorage.setItem('auth_token', newToken);
        setToken(newToken);
        setUser({
          username: returnedUsername,
          fullName: fullName || returnedUsername,
          userType: (userType as any) || 'admin',
          isMainAccount: isMainAccount || false,
          isAdmin: true
        });

        return true;
      } else {
        console.error('Refresh failed:', data.error);
        return false;
      }
    } catch (error) {
      console.error('Refresh user error:', error);
      return false;
    }
  };

  const value: AuthContextType = {
    user,
    token,
    login,
    logout,
    refreshUser,
    isLoading
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
